import * as React from 'react'
import { connect } from 'react-redux'

import {
  PLAYLIST_THEMES_LIMIT_AMOUNT,
  PLAYLIST_THEMES_UNLIMITED,
} from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getFeatureData } from '@disco/state-management/selectors/business'
import type { TrialLeft } from '@disco/utils/trial'

import type { PlaylistTheme } from 'types/playlist'
import type { State } from 'types/redux'

import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import EditTheme from 'components/edit-theme'
import ThemeExplorer from 'components/theme-explorer'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import InlineLink from 'components/ui/inline-link'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'
import Tooltip from 'components/ui/tooltip'

function mapStateToProps(state: State): any {
  const { isTrial, trialLeft, isFeatureEnabled } = getFeatureData(state, {
    featureName: PLAYLIST_THEMES_UNLIMITED,
  })
  return {
    ...state.themes,
    trialLeft,
    isTrial,
    themesLimit: !isFeatureEnabled,
    themesLoaded: state.themes.isFetched,
    isPhone: isDeviceTypePhone(state),
  }
}

const mapDispatchToProps = {
  sendMixpanelEvent,
}

class ThemesSettings extends React.Component<
  {
    onSelect?: (arg0: PlaylistTheme) => unknown
    sendMixpanelEvent: (...args: Array<any>) => any
    trialLeft: TrialLeft
    isTrial: boolean
    themes: PlaylistTheme[]
    themesLimit: boolean
    themesLoaded: boolean
    isPhone: boolean
  },
  {
    modalOpen: boolean
  }
> {
  state = {
    modalOpen: false,
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open Themes tab in settings')
  }

  _openModal = () => {
    this.setState({
      modalOpen: true,
    })
  }
  _closeModal = () => {
    this.setState({
      modalOpen: false,
    })
  }
  _onSave = (theme: PlaylistTheme) => {
    if (this.props.onSelect) {
      this.props.onSelect(theme)
    }
  }
  _actionWrapper = (children: React.ReactNode) => {
    const { themesLimit, themes } = this.props
    const isDisableAction =
      themesLimit && themes.length >= PLAYLIST_THEMES_LIMIT_AMOUNT

    if (isDisableAction) {
      return (
        <Tooltip
          content={
            <>
              Want more than {PLAYLIST_THEMES_LIMIT_AMOUNT} themes?
              <br />
              <PricingBadge title="UPGRADE" />
            </>
          }
        >
          {children}
        </Tooltip>
      )
    }

    return children
  }

  render() {
    const { isTrial, trialLeft, themes, themesLimit, themesLoaded, isPhone } =
      this.props
    const isDisableAction =
      themesLimit && themes.length >= PLAYLIST_THEMES_LIMIT_AMOUNT
    return (
      <>
        <DocumentHeadTags title="Settings | Themes | DISCO" />
        <PageHeader
          title={
            <>
              Playlist Themes
              <HelpTooltip id="tooltip_settings_themes" />
            </>
          }
          actions={
            !isPhone
              ? this._actionWrapper(
                  <Button
                    appearance="icon"
                    icon="add-circle"
                    disabled={isDisableAction || !themesLoaded}
                    tooltip={!isDisableAction ? 'New Theme' : ''}
                    onClick={!isDisableAction ? this._openModal : null}
                  />,
                )
              : undefined
          }
        />
        <PageContainer>
          <ThemeExplorer fullWidth />
          {themesLimit && (
            <Alert icon="warning" status="warning">
              {isTrial ? (
                <>
                  Heads up! {trialLeft.message}
                  <br />
                  <InlineLink to="/settings/billing">Plus and Pro</InlineLink>
                  &nbsp;plans allow unlimited themes. The Lite plan is limited
                  to {PLAYLIST_THEMES_LIMIT_AMOUNT}.
                </>
              ) : (
                <>
                  Want more than {PLAYLIST_THEMES_LIMIT_AMOUNT} themes?&nbsp;
                  <InlineLink to="/settings/billing">Upgrade</InlineLink>
                </>
              )}
            </Alert>
          )}
          <DiscoModal
            isOpen={this.state.modalOpen}
            onRequestClose={this._closeModal}
          >
            <EditTheme
              theme={null}
              onSave={this._onSave}
              closeModal={this._closeModal}
            />
          </DiscoModal>
        </PageContainer>
      </>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ThemesSettings) as any
